/* eslint-disable no-process-env */
import * as Sentry from '@sentry/react';
import {type Breadcrumb} from '@sentry/react';
import {parse} from '@tinkoff/url';

import type {User} from '@sentry/types';
import {type Reporter} from '@tinkoff/logger';

import type {TelemetryHandler} from 'packages/mattermost-redux/src/client/telemetry';

const sentryEnabled = process.env.SENTRY_ENABLED;

let sentryInitialized = false;

export function initSentry() {
    if (sentryEnabled && !sentryInitialized) {
        Sentry.init({
            dsn: process.env.SENTRY_DSN,
            integrations: [],
            autoSessionTracking: false,
            enableAutoSessionTracking: false,
            enableAutoPerformanceTracking: false,
            environment: typeof window === 'undefined' ? 'development' : parse(window.location.href).hostname,
            release: process.env.SENTRY_RELEASE || 'dev',

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: 0.001,

            // Add this ignore as it is ignored in webapp
            // @see line 50
            ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications.',
            ],
            beforeBreadcrumb(breadcrumb) {
                if (Array.isArray(breadcrumb.data?.arguments)) {
                    const breadcrumbArgs = breadcrumb.data!.arguments.filter(
                        (arg) => typeof arg !== 'object' && typeof arg !== 'undefined',
                    );
                    return {
                        ...breadcrumb,
                        data: {
                            ...breadcrumb.data,
                            arguments: breadcrumbArgs,
                        },
                    };
                }
                return breadcrumb;
            },
        } as Sentry.BrowserOptions);
        sentryInitialized = true;
    }
}

export function setSentryUser(user: User) {
    if (sentryInitialized) {
        Sentry.setUser(user);
    }
}

export function setSentryServer(serverUrl: string) {
    if (sentryInitialized) {
        Sentry.setExtra('selectedServerUrl', serverUrl);
    }
}

function addBreadcrumbToSentry(breadcrumb: Breadcrumb) {
    if (sentryInitialized) {
        Sentry.addBreadcrumb(breadcrumb);
    }
}

export class SentryBreadcrumbsTelemetry implements TelemetryHandler {
    pageVisited = (userId: string, userRoles: string, category: string, name: string) => {
        addBreadcrumbToSentry({
            category,
            type: 'navigation',
            level: 'info',
            timestamp: Date.now(),
            message: name,
            data: {
                userRoles,
                userId,
            },
        });
    }
    trackEvent = (userId: string, userRoles: string, category: string, event: string, props = {}) => {
        addBreadcrumbToSentry({
            category,
            type: 'info',
            message: event,
            level: 'info',
            timestamp: Date.now(),
            data: {
                userRoles,
                userId,
                ...props,
            },
        });
    }
}

export const sentryReporter: Reporter = {
    log(logObj) {
        if (!sentryInitialized || logObj.type !== 'error') {
            return;
        }

        const [error, context] = logObj.args;

        if (typeof error === 'string') {
            Sentry.captureMessage(error, context);
            return;
        }

        Sentry.captureException(error, context);
    },
};
